'use client';

import {
  useEffect,
  useLayoutEffect,
  useState,
  type CSSProperties,
  type RefObject,
} from 'react';

/**
 * Fixed-position style for a panel anchored to a trigger, portaled to <body>
 * so it can never be clipped by modal/table overflow. Flips above the trigger
 * near the viewport bottom; closes on outside click, Escape, resize, and
 * scrolling outside the panel.
 */
export function useAnchoredPanel(
  open: boolean,
  anchorRef: RefObject<HTMLElement | null>,
  panelRef: RefObject<HTMLElement | null>,
  onClose: () => void,
  opts: { estimatedHeight?: number; align?: 'start' | 'end' } = {}
): CSSProperties | null {
  const { estimatedHeight = 380, align = 'start' } = opts;
  const [style, setStyle] = useState<CSSProperties | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setStyle(null);
      return;
    }
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return;
    const openUp = rect.bottom + estimatedHeight > window.innerHeight && rect.top > estimatedHeight;
    setStyle({
      position: 'fixed',
      zIndex: 60,
      ...(openUp ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
      ...(align === 'end'
        ? { right: Math.max(8, window.innerWidth - rect.right) }
        : { left: Math.min(rect.left, Math.max(8, window.innerWidth - 340)) }),
    });
  }, [open, anchorRef, estimatedHeight, align]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (anchorRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      // Clicks inside another floating panel (e.g. a Combobox portaled to <body>
      // and rendered from within this panel) are not "outside" — they belong to
      // nested UI, so don't self-close on them.
      if (e.target instanceof Element && e.target.closest('[data-floating-panel]')) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const onScroll = (e: Event) => {
      if (panelRef.current?.contains(e.target as Node)) return;
      if (e.target instanceof Element && e.target.closest('[data-floating-panel]')) return;
      onClose();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onKey as never);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onKey as never);
    };
  }, [open, anchorRef, panelRef, onClose]);

  return style;
}
